import React, { useState } from 'react'
import { toast } from 'react-toastify'

import { Button, Dialog } from '@clientbase/clientbase-library'
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Theme,
  useMediaQuery
} from '@mui/material'
import { DataService } from 'api/DataService'
import { useAccountsPayableContext } from 'templates/AccountsPayable/context'

import { toastProps } from 'utils/types/toast-props'

import { theme } from 'styles/theme'

interface AcionsProps {
  uuid: string
  status: string
  isRecurrence: boolean
  hasPaidExpensesAssociated?: boolean
  cycle?: number
  amount: string
  dueDate?: string
}
const Actions = ({
  uuid,
  status,
  isRecurrence,
  hasPaidExpensesAssociated,
  cycle,
  amount,
  dueDate
}: AcionsProps) => {
  const [isOpenDialog, setIsOpenDialog] = useState<boolean>(false)
  const [deleteType, setIsDeleteType] = useState<number>(0)
  const [isOpenDialogDeletePaidExpenses, setIsOpenDialogDeletePaidExpenses] =
    useState<boolean>(false)

  const {
    setReloadTable,
    reloadTable,
    setExpenseMarkAsPaidModal,
    expenseDetailModal,
    setExpenseDetailModal,
    setExpenseFormModal
  } = useAccountsPayableContext()
  const handleClickMarkAsPaid = (uuidExpense: string) => {
    setExpenseMarkAsPaidModal({
      isOpen: true,
      uuid: uuidExpense,
      amount: amount,
      dueDate: dueDate
    })
  }

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down(750))

  const handleDestroy = () => {
    if (isRecurrence) {
      handleDestroyRecurrence()
    } else {
      handleDestroyExpense()
    }
  }

  const handleDestroyExpense = async () => {
    const id = toast.loading(
      `Excluindo despesa ${isRecurrence ? 'recorrente...' : '...'}`
    )
    const response = await DataService({
      url: isRecurrence
        ? `/v1/accounts_payables_recurrences/${uuid}`
        : `/v1/accounts_payables/${uuid}`,
      type: 'DELETE'
    })
    if (expenseDetailModal.isOpen) {
      setExpenseDetailModal({ isOpen: false, uuid: '' })
    }

    setReloadTable(!reloadTable)
    toast.update(id, {
      render: response.error
        ? `Ocorreu um erro ao excluir a despesa ${
            isRecurrence ? 'recorrente' : ''
          }`
        : `Despesa ${isRecurrence ? 'recorrente' : ''} excluída com sucesso`,
      type: response.error ? 'error' : 'success',
      ...toastProps
    })
  }
  const handleDestroyRecurrence = async () => {
    const id = toast.loading('Excluindo despesa recorrente...')
    const response = await DataService({
      url:
        deleteType === 1 || !hasPaidExpensesAssociated
          ? `/v1/accounts_payable_recurrences/${uuid}`
          : `/v1/accounts_payable_recurrences/${uuid}/cancel`,
      ...(deleteType === 1 && {
        data: {
          force_destroy: true
        }
      }),
      type: deleteType === 1 || !hasPaidExpensesAssociated ? 'DELETE' : 'PATCH'
    })
    setExpenseDetailModal({ isOpen: false, uuid: '' })
    setReloadTable(!reloadTable)
    toast.update(id, {
      render: response.error
        ? 'Ocorreu um erro ao excluir a despesa recorrente'
        : 'Despesa recorrente excluída com sucesso',
      type: response.error ? 'error' : 'success',
      ...toastProps
    })
  }

  return (
    <>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="end"
        width={'100%'}
        gap={1}
      >
        <>
          {status !== 'cancelled' && (
            <>
              <Button
                onClick={() =>
                  setExpenseFormModal({
                    isOpen: true,
                    uuid: expenseDetailModal.uuid,
                    isRecurrence: isRecurrence
                  })
                }
                sx={{
                  [theme.breakpoints.down(450)]: {
                    width: '100%'
                  }
                }}
                variantButton="neutral"
                style={{
                  outline: 'none',
                  borderRadius: '8px'
                }}
              >
                Editar
              </Button>
              <Button
                onClick={() =>
                  hasPaidExpensesAssociated
                    ? setIsOpenDialogDeletePaidExpenses(true)
                    : setIsOpenDialog(true)
                }
                sx={{
                  [theme.breakpoints.down(450)]: {
                    width: '100%'
                  }
                }}
                variantButton="neutral"
                style={{
                  outline: 'none',
                  borderRadius: '8px'
                }}
              >
                Excluir
              </Button>
            </>
          )}

          {/* Despesa paga não repete o botão aqui: a ação de desfazer a baixa mora no
              accordion "Detalhes do pagamento", junto dos dados que ela desfaz, como a
              cobrança faz no PaymentDetails do ModalChargeDetail. */}
          {!isRecurrence && status !== 'paid' && (
            <Button
              variantButton="primary"
              onClick={() => handleClickMarkAsPaid(uuid)}
              sx={{
                [theme.breakpoints.down(450)]: {
                  width: '100%'
                }
              }}
            >
              Marcar como paga
            </Button>
          )}
        </>
      </Stack>

      <Dialog
        title={`Confirma excluir ${isRecurrence ? 'recorrência' : 'despesa'}?`}
        description={
          // Despesa avulsa não tem ciclo: o aviso de recorrente só vale para
          // um ciclo de recorrência (cycle definido).
          !isRecurrence && cycle != null
            ? `Você está excluindo apenas o ciclo ${cycle} dessa despesa recorrente. Para excluir todas as despesas acesse a recorrência`
            : ''
        }
        maxWidth="xs"
        icon="delete"
        isOpenDialog={isOpenDialog}
        setIsOpenDialog={setIsOpenDialog}
        cancelButton
      >
        <Button fullWidth onClick={handleDestroy}>
          Excluir
        </Button>
      </Dialog>

      <Dialog
        title="Exclusão de recorrências"
        description="Esta recorrência possui despesas pagas associadas."
        icon="info"
        onClose={() => setIsOpenDialog(false)}
        isOpenDialog={isOpenDialogDeletePaidExpenses}
        setIsOpenDialog={setIsOpenDialogDeletePaidExpenses}
        PaperProps={{ sx: { maxWidth: '450px' } }}
      >
        <Box display="flex" alignItems="center" flexDirection="column" gap={1}>
          <RadioGroup
            aria-labelledby="group-type"
            value={deleteType}
            onChange={(event) => setIsDeleteType(Number(event.target.value))}
            sx={{ pb: 2 }}
          >
            <FormControlLabel
              value={0}
              control={<Radio />}
              label="Cancelar recorrência e não excluir as despesas pagas."
            />
            <FormControlLabel
              value={1}
              control={<Radio />}
              label="Cancelar recorrência e excluir todas as despesas."
            />
          </RadioGroup>
          <Box width="100%" justifyContent="end" display="flex" gap={1}>
            <Button
              fullWidth
              variantButton="neutral"
              onClick={() => setIsOpenDialog(false)}
            >
              Cancelar
            </Button>
            <Button fullWidth onClick={handleDestroy}>
              Confirmar
            </Button>
          </Box>
        </Box>
      </Dialog>
    </>
  )
}

export default Actions
